import React, { useCallback, useEffect, useState } from 'react';
import {
    Paper, Typography, Box, Button, TextField, MenuItem, Chip,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer, TablePagination,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
    getWireOrdersService, getWireProofService, reviewWireOrderService,
    getBankPaymentsService, syncBankPaymentsService, resolveBankPaymentService,
} from '../../../../services/billing.service';
import { toastSuccess, toastWarning } from '../../../../utils/utils';

/**
 * BankTransfersTab — review bank-transfer payments for plans and DQ Token top-ups.
 *
 * A buyer chooses bank transfer in the portal, pays quoting the Stripe invoice
 * number, and uploads proof. Approving grants the DQ Tokens, activates a bought
 * plan and marks the Stripe invoice paid. Rejecting fails the order and voids the
 * invoice; the note is shown to the buyer, so it is required.
 *
 * When Wise is connected, payments that arrive there are matched to these orders by
 * invoice number, and a clear match approves its order by itself (Payment Settings →
 * automatic approval). "Payments received in Wise" lists every payment and why one
 * was left for review; an admin links it to an order (approving that order with it)
 * or sets it aside.
 */

const STATUS = {
    awaiting_proof: { label: 'Awaiting proof', color: '#92400E', bg: '#FEF3C7' },
    under_review:   { label: 'Under review',   color: '#1E40AF', bg: '#DBEAFE' },
    approved:       { label: 'Approved',       color: '#166534', bg: '#DCFCE7' },
    rejected:       { label: 'Rejected',       color: '#991B1B', bg: '#FEE2E2' },
    cancelled:      { label: 'Cancelled by buyer', color: '#374151', bg: '#F3F4F6' },
};

const fmtUsd = (n) => `US$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDqt = (n) => `${Number(n || 0).toLocaleString('en-US')} DQT`;
const fmtDate = (d) => (d ? new Date(d).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const openBase64 = ({ file_name, mime_type, content_base64 }) => {
    const bytes = Uint8Array.from(atob(content_base64), (c) => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], { type: mime_type || 'application/octet-stream' }));
    const win = window.open(url, '_blank', 'noopener');
    if (!win) {
        // Pop-up blocked — download instead.
        const a = document.createElement('a');
        a.href = url;
        a.download = file_name || 'proof';
        document.body.appendChild(a);
        a.click();
        a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
};

const StatusChip = ({ status }) => {
    const st = STATUS[status] || { label: status || '—', color: '#374151', bg: '#F3F4F6' };
    return <Chip size="small" label={st.label} style={{ background: st.bg, color: st.color, fontWeight: 700 }}/>;
};

const PAYMENT_STATUS = {
    needs_review:  { label: 'To review',              color: '#92400E', bg: '#FEF3C7' },
    auto_approved: { label: 'Approved automatically', color: '#166534', bg: '#DCFCE7' },
    linked:        { label: 'Linked by an admin',     color: '#166534', bg: '#DCFCE7' },
    ignored:       { label: 'Set aside',              color: '#374151', bg: '#F3F4F6' },
};

/** Payments received in Wise, and the ones that could not approve an order by themselves. */
const WisePaymentsPanel = ({ onChanged }) => {
    const [status, setStatus] = useState('needs_review');
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [rows, setRows] = useState([]);
    const [resolve, setResolve] = useState(null);    // { payment, action: 'link' | 'ignore' }
    const [openOrders, setOpenOrders] = useState([]);
    const [orderId, setOrderId] = useState('');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getBankPaymentsService({ status: status || undefined, page: 1, limit: 50 });
            setRows(res?.data?.transactions || []);
        } catch (err) {
            console.error('[WisePaymentsPanel] load failed:', err);
        } finally {
            setLoading(false);
        }
    }, [status]);

    useEffect(() => { load(); }, [load]);

    const sync = async () => {
        setSyncing(true);
        try {
            const res = await syncBankPaymentsService({});
            const s = res?.data || {};
            toastSuccess(`Checked Wise: ${s.recorded || 0} new payment(s), ${s.auto_approved || 0} approved automatically, ${s.needs_review || 0} to review.`);
            await load();
            if (onChanged) onChanged();
        } catch (err) {
            // invokeApi has already shown the API's reason (e.g. Wise not connected).
            console.error('[WisePaymentsPanel] sync failed:', err);
        } finally {
            setSyncing(false);
        }
    };

    const startResolve = async (payment, action) => {
        setNote('');
        setOrderId(payment.order_id || payment.candidate_order_ids?.[0] || '');
        setResolve({ payment, action });
        if (action !== 'link') return;
        try {
            const [waiting, reviewing] = await Promise.all([
                getWireOrdersService({ status: 'awaiting_proof', page: 1, limit: 100 }),
                getWireOrdersService({ status: 'under_review', page: 1, limit: 100 }),
            ]);
            setOpenOrders([...(reviewing?.data?.orders || []), ...(waiting?.data?.orders || [])]);
        } catch (err) {
            console.error('[WisePaymentsPanel] loading open orders failed:', err);
        }
    };

    const submit = async () => {
        if (!resolve) return;
        if (resolve.action === 'ignore' && !note.trim()) { toastWarning('Say why this payment is set aside.'); return; }
        if (resolve.action === 'link' && !orderId) { toastWarning('Choose the order this payment is for.'); return; }
        setSaving(true);
        try {
            await resolveBankPaymentService({
                transaction_id: resolve.payment.id, action: resolve.action,
                order_id: resolve.action === 'link' ? orderId : undefined, note: note.trim() || undefined,
            });
            toastSuccess(resolve.action === 'link' ? 'Order approved with this payment.' : 'Payment set aside.');
            setResolve(null);
            await load();
            if (onChanged) onChanged();
        } catch (err) {
            console.error('[WisePaymentsPanel] resolve failed:', err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Paper style={{ padding: 24, marginBottom: 24 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 16 }} mb={2}>
                <Box>
                    <Typography variant="h6" style={{ fontWeight: 700 }}>Payments received in Wise</Typography>
                    <Typography variant="body2" color="textSecondary">
                        A payment that quotes one open invoice and pays its amount approves the order by itself. The rest wait here.
                    </Typography>
                </Box>
                <Box display="flex" style={{ gap: 12 }}>
                    <TextField select size="small" variant="outlined" label="Show" value={status}
                        onChange={(e) => setStatus(e.target.value)} style={{ minWidth: 200 }}>
                        <MenuItem value="">All</MenuItem>
                        {Object.entries(PAYMENT_STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                    </TextField>
                    <Button variant="outlined" color="primary" onClick={sync} disabled={syncing}>
                        {syncing ? <CircularProgress size={18}/> : 'Check Wise now'}
                    </Button>
                </Box>
            </Box>

            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Received</TableCell>
                            <TableCell>From</TableCell>
                            <TableCell>Reference</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell>Outcome</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={24}/></TableCell></TableRow>
                        ) : rows.length === 0 ? (
                            <TableRow><TableCell colSpan={6} align="center">
                                <Typography variant="body2" color="textSecondary">No payments.</Typography>
                            </TableCell></TableRow>
                        ) : rows.map((p) => {
                            const st = PAYMENT_STATUS[p.match_status] || { label: p.match_status, color: '#374151', bg: '#F3F4F6' };
                            return (
                                <TableRow key={p.id}>
                                    <TableCell>{fmtDate(p.received_at)}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{p.sender_name || '—'}</Typography>
                                        {p.sender_account && <Typography variant="caption" color="textSecondary">{p.sender_account}</Typography>}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{p.payment_reference || p.description || '—'}</Typography>
                                        <Typography variant="caption" color="textSecondary">{p.wise_reference}</Typography>
                                    </TableCell>
                                    <TableCell align="right">{p.amount_display}</TableCell>
                                    <TableCell>
                                        <Chip size="small" label={st.label} style={{ background: st.bg, color: st.color, fontWeight: 700 }}/>
                                        {p.review_reason_text && (
                                            // Shadow mode: the rules would have approved it — usually a quick Link to order.
                                            <Typography variant="caption" component="div"
                                                style={p.review_reason === 'would_approve' ? { color: '#166534', fontWeight: 700 } : undefined}>
                                                {p.review_reason_text}
                                            </Typography>
                                        )}
                                        {p.order_id && <Typography variant="caption" color="textSecondary" component="div">Order {p.order_id}</Typography>}
                                        {p.resolution_note && <Typography variant="caption" component="div">“{p.resolution_note}”</Typography>}
                                    </TableCell>
                                    <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                                        {p.match_status === 'needs_review' && (
                                            <>
                                                <Button size="small" variant="contained" color="primary" style={{ marginRight: 8 }}
                                                    onClick={() => startResolve(p, 'link')}>
                                                    Link to order
                                                </Button>
                                                <Button size="small" variant="outlined" onClick={() => startResolve(p, 'ignore')}>
                                                    Set aside
                                                </Button>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={!!resolve} onClose={() => !saving && setResolve(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{resolve?.action === 'link' ? 'Link payment to an order' : 'Set payment aside'}</DialogTitle>
                <DialogContent>
                    {resolve && (
                        <>
                            <Typography variant="body2" gutterBottom>
                                <strong>{resolve.payment.amount_display}</strong> from {resolve.payment.sender_name || 'an unknown sender'},
                                reference “{resolve.payment.payment_reference || resolve.payment.description || '—'}”.
                            </Typography>
                            {resolve.action === 'link' ? (
                                <>
                                    <Typography variant="body2" color="textSecondary" gutterBottom>
                                        Linking approves the order with this payment — whatever the amount — granting its DQ Tokens and marking its invoice paid.
                                    </Typography>
                                    <TextField
                                        select fullWidth variant="outlined" size="small" style={{ marginTop: 12 }}
                                        label="Order" value={orderId} onChange={(e) => setOrderId(e.target.value)}
                                    >
                                        {openOrders.map((o) => (
                                            <MenuItem key={o.order_id} value={o.order_id}>
                                                {o.invoice_no} · {o.created_by?.email || o.dq_insider_id} · {fmtUsd(o.amount_usd)}
                                                {o.wire?.quote && o.wire.quote.currency !== 'USD' ? ` (${o.wire.quote.amount_display})` : ''}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </>
                            ) : null}
                            <TextField
                                fullWidth multiline minRows={2} variant="outlined" size="small" style={{ marginTop: 12 }}
                                label={resolve.action === 'link' ? 'Note (optional)' : 'Why it is set aside'}
                                required={resolve.action === 'ignore'}
                                value={note} onChange={(e) => setNote(e.target.value.slice(0, 500))}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResolve(null)} disabled={saving}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} variant="contained" color="primary">
                        {saving ? <CircularProgress size={18}/> : (resolve?.action === 'link' ? 'Link and approve' : 'Set aside')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

const BankTransfersTab = () => {
    const [status, setStatus] = useState('under_review');
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(20);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ orders: [], total: 0 });
    const [review, setReview] = useState(null);      // { order, action }
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [proofBusy, setProofBusy] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getWireOrdersService({ status: status || undefined, page: page + 1, limit });
            setData({ orders: res?.data?.orders || [], total: res?.data?.total || 0 });
        } catch (err) {
            console.error('[BankTransfersTab] load failed:', err);
        } finally {
            setLoading(false);
        }
    }, [status, page, limit]);

    useEffect(() => { load(); }, [load]);

    const handleProof = async (order, index) => {
        const key = `${order.order_id}:${index}`;
        setProofBusy(key);
        try {
            const res = await getWireProofService(order.order_id, index);
            if (res?.data?.content_base64) openBase64(res.data);
        } catch (err) {
            console.error('[BankTransfersTab] proof failed:', err);
        } finally {
            setProofBusy(null);
        }
    };

    const handleReview = async () => {
        if (!review) return;
        if (review.action === 'reject' && !note.trim()) { toastWarning('Say why the transfer is rejected — the buyer sees this note.'); return; }
        setSaving(true);
        try {
            const res = await reviewWireOrderService({ order_id: review.order.order_id, action: review.action, note: note.trim() || undefined });
            const o = res?.data?.order;
            if (review.action === 'approve') {
                toastSuccess(`Approved — ${fmtDqt(o?.dqt_amount)} granted${o?.plan_activated ? ` and ${o.label} activated` : ''}.`);
                if (o?.fulfilment_error) toastWarning(`The payment is recorded but needs attention: ${o.fulfilment_error}`);
                else if (o?.invoice_error) toastWarning(`DQ Tokens granted, but the Stripe invoice could not be marked paid: ${o.invoice_error}`);
            } else {
                toastSuccess('Transfer rejected. The buyer has been shown your note.');
            }
            setReview(null);
            setNote('');
            await load();
        } catch (err) {
            // invokeApi has already shown the API's reason.
            console.error('[BankTransfersTab] review failed:', err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
        <WisePaymentsPanel onChanged={load}/>
        <Paper style={{ padding: 24 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 16 }} mb={2}>
                <Box>
                    <Typography variant="h6" style={{ fontWeight: 700 }}>Bank transfers</Typography>
                    <Typography variant="body2" color="textSecondary">
                        Check the proof against the bank statement before approving. Approval grants the DQ Tokens and cannot be undone here.
                    </Typography>
                </Box>
                <TextField select size="small" variant="outlined" label="Status" value={status}
                    onChange={(e) => { setStatus(e.target.value); setPage(0); }} style={{ minWidth: 200 }}>
                    <MenuItem value="">All</MenuItem>
                    {Object.entries(STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                </TextField>
            </Box>

            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Requested</TableCell>
                            <TableCell>Workspace</TableCell>
                            <TableCell>Purchase</TableCell>
                            <TableCell>Invoice</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell>Proof</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={8} align="center"><CircularProgress size={24}/></TableCell></TableRow>
                        ) : data.orders.length === 0 ? (
                            <TableRow><TableCell colSpan={8} align="center">
                                <Typography variant="body2" color="textSecondary">No bank transfers.</Typography>
                            </TableCell></TableRow>
                        ) : data.orders.map((o) => (
                            <TableRow key={o.order_id}>
                                <TableCell>{fmtDate(o.created_at)}</TableCell>
                                <TableCell>
                                    <Typography variant="body2">{o.created_by?.email || '—'}</Typography>
                                    <Typography variant="caption" color="textSecondary">{o.dq_insider_id}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">{o.kind === 'plan' ? `Plan — ${o.label}` : `Top-up — ${o.label}`}</Typography>
                                    <Typography variant="caption" color="textSecondary">{fmtDqt(o.dqt_amount)} at US${o.usd_per_dqt}</Typography>
                                </TableCell>
                                <TableCell>{o.invoice_no || '—'}</TableCell>
                                <TableCell align="right">
                                    {fmtUsd(o.amount_usd)}
                                    {o.wire?.quote && o.wire.quote.currency !== 'USD' && (
                                        <Typography variant="caption" color="textSecondary" component="div">quoted {o.wire.quote.amount_display}</Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {(o.wire?.proofs || []).length === 0 ? '—' : o.wire.proofs.map((p, i) => (
                                        <Button key={i} size="small" color="primary" onClick={() => handleProof(o, i)}
                                            disabled={proofBusy === `${o.order_id}:${i}`} style={{ textTransform: 'none', display: 'block' }}>
                                            {proofBusy === `${o.order_id}:${i}` ? <CircularProgress size={14}/> : (p.file_name || `File ${i + 1}`)}
                                        </Button>
                                    ))}
                                    {o.wire?.buyer_note ? <Typography variant="caption" color="textSecondary">“{o.wire.buyer_note}”</Typography> : null}
                                </TableCell>
                                <TableCell>
                                    <StatusChip status={o.wire?.status}/>
                                    {o.wire?.reviewed_at && (
                                        <Typography variant="caption" color="textSecondary" component="div">
                                            {fmtDate(o.wire.reviewed_at)}{o.wire.reviewed_by?.label ? ` · ${o.wire.reviewed_by.label}` : ''}
                                        </Typography>
                                    )}
                                    {o.wire?.review_note && <Typography variant="caption" component="div">“{o.wire.review_note}”</Typography>}
                                    {o.wire?.bank_match && (
                                        <Typography variant="caption" color="textSecondary" component="div">
                                            Wise {o.wire.bank_match.wise_reference}{o.wire.bank_match.matched === 'auto' ? ' · matched automatically' : ''}
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                                    {o.wire?.status === 'under_review' && (
                                        <Button size="small" variant="contained" color="primary" style={{ marginRight: 8 }}
                                            onClick={() => { setNote(''); setReview({ order: o, action: 'approve' }); }}>
                                            Approve
                                        </Button>
                                    )}
                                    {['under_review', 'awaiting_proof'].includes(o.wire?.status) && (
                                        <Button size="small" variant="outlined" style={{ color: '#b91c1c', borderColor: '#fca5a5' }}
                                            onClick={() => { setNote(''); setReview({ order: o, action: 'reject' }); }}>
                                            Reject
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div" count={data.total} page={page} rowsPerPage={limit}
                onChangePage={(_, p) => setPage(p)}
                onPageChange={(_, p) => setPage(p)}
                onChangeRowsPerPage={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
                onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 20, 50]}
            />

            <Dialog open={!!review} onClose={() => !saving && setReview(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{review?.action === 'approve' ? 'Approve bank transfer' : 'Reject bank transfer'}</DialogTitle>
                <DialogContent>
                    {review && (
                        <>
                            <Typography variant="body2" gutterBottom>
                                <strong>{fmtUsd(review.order.amount_usd)}</strong> for {review.order.kind === 'plan' ? `the ${review.order.label} plan` : review.order.label},
                                invoice <strong>{review.order.invoice_no}</strong>.
                            </Typography>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                                {review.action === 'approve'
                                    ? `Approving grants ${fmtDqt(review.order.dqt_amount)}${review.order.kind === 'plan' ? ', activates the plan' : ''} and marks the Stripe invoice paid outside Stripe.`
                                    : 'Rejecting closes this order and voids its Stripe invoice. Nothing is granted. The buyer sees your note.'}
                            </Typography>
                            <TextField
                                fullWidth multiline minRows={3} variant="outlined" size="small" style={{ marginTop: 12 }}
                                label={review.action === 'approve' ? 'Note (optional)' : 'Reason shown to the buyer'}
                                required={review.action === 'reject'}
                                value={note} onChange={(e) => setNote(e.target.value.slice(0, 500))}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReview(null)} disabled={saving}>Cancel</Button>
                    <Button onClick={handleReview} disabled={saving} variant="contained"
                        style={review?.action === 'reject' ? { background: '#b91c1c', color: '#fff' } : undefined}
                        color={review?.action === 'approve' ? 'primary' : 'default'}>
                        {saving ? <CircularProgress size={18}/> : (review?.action === 'approve' ? 'Approve and grant' : 'Reject')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
        </>
    );
};

export default BankTransfersTab;
